import at from "lodash/at";
import has from "lodash/has";
import filter from "lodash/filter";
import find from "lodash/find";
import flattenDeep from "lodash/flattenDeep";
import isNull from "lodash/isNull";
import isArray from "lodash/isArray";
import get from "lodash/get";
import {
  addDays,
  addWeeks,
  differenceInMinutes,
  isBefore,
  isSameDay,
  isWithinInterval,
  minutesToHours,
  subDays,
} from "date-fns";
import {
  getActionIconIDTextAsClass,
  getFontAwesomeIconFromActivity,
} from "../constants/pcs-functions";
import { reportEvents } from "../constants/activity-report-statuses";

import config from "../../config";

const SUPPORT_ACTIVITIES_TITLE = "Support";
const CARE_ACTIVITIES_TITLE = "Care";
const SUPPORT_TYPE = "support";
const CARE_TYPE = "care";

/* v8 ignore start */
/**
 * Not all rotas should have activities - this function returns whether it should or not.
 *
 * @param {Object} rota
 * @returns {Boolean}
 */
function rotaHasActivities(rota) {
  return (
    config.rotaConfiguration[rota.type].hasSupportActivities ||
    config.rotaConfiguration[rota.type].hasActivities
  );
}
/* v8 ignore stop */

/**
 * Returns the activities (sorted) that match the specified rota start date and day index
 *
 * note - this doesn't include activities that start on the previous day and end this day.
 *
 * @param {[Boolean, Array]} activities
 * @param {Date} rotaStartDate
 * @param {Number} dayIndex
 * @returns {Array}
 */
function getActivitiesForRotaDay(activities, rotaStartDate, dayIndex) {
  if (activities === false) {
    return [];
  }
  const theDay = addDays(rotaStartDate, dayIndex);
  const result = activities.filter((act) => isSameDay(act.startDate, theDay));
  return result.sort((a, b) => a.startDate - b.startDate);
}

/**
 * Returns the activities that have not been assigned in the rota
 *
 * @param {[Boolean, Array]} activities
 * @param {Object} rota
 * @returns {Array}
 */
function getActivitiesNotAssignedInRota(activities, rota) {
  if (activities === false) {
    return [];
  }
  if (!rota.shifts) {
    return activities;
  }
  // unwrap the nested shifts[].activities[] so that we just have an array of activtyId
  const assignedActivityIds = filter(
    flattenDeep(
      rota.shifts.map((shift) => {
        if (has(shift, "activities")) {
          return shift.activities.map((v) => v.activityId);
        }
        return null;
      }),
    ),
    (item) => !isNull(item),
  );
  return activities.filter(
    (activity) => assignedActivityIds.indexOf(activity.id) === -1,
  );
}

/**
 * Returns the activities that have been assigned in the rota
 *
 * @param {[Boolean, Array]} activities
 * @param {Object} rota
 * @returns {Array}
 */
function getActivitiesAssignedInRota(activities, rota) {
  if (activities === false) {
    return [];
  }
  if (!rota.shifts) {
    return [];
  }
  // unwrap the nested shifts[].activities[] so that we just have an array of activtyId
  const assignedActivityIds = filter(
    flattenDeep(
      rota.shifts.map((shift) => {
        if (has(shift, "activities")) {
          return shift.activities.map((v) => v.activityId);
        }
        return null;
      }),
    ),
    (item) => !isNull(item),
  );
  return activities.filter(
    (activity) => assignedActivityIds.indexOf(activity.id) !== -1,
  );
}

/**
 * Returns the activities that are not support activities
 *
 * @param {[Boolean, Array]} activities
 * @returns {Array}
 */
function getActivitiesWithoutSupport(activities) {
  if (activities === false) {
    return [];
  }
  return activities.filter((activity) => {
    if (activity.type) {
      return activity.type !== SUPPORT_TYPE;
    }
    return activity.title !== SUPPORT_ACTIVITIES_TITLE;
  });
}

/**
 * Returns the activities that are not support activities
 *
 * @param {[Boolean, Array]} activities
 * @returns {Array}
 */
function getActivitiesWithoutCare(activities) {
  if (activities === false) {
    return [];
  }
  return activities.filter((activity) => {
    if (activity.type) {
      return activity.type !== CARE_TYPE;
    }
    return activity.title !== CARE_ACTIVITIES_TITLE;
  });
}

/**
 * Returns the activities that are support activities
 *
 * @param {[Boolean, Array]} activities
 * @returns {Array}
 */
function getSupportActivities(activities) {
  if (activities === false) {
    return [];
  }
  return activities.filter(
    (activity) =>
      activity?.type === SUPPORT_TYPE ||
      activity.title === SUPPORT_ACTIVITIES_TITLE,
  );
}

function getActivityType(activity) {
  if (activity.title === SUPPORT_ACTIVITIES_TITLE) {
    return SUPPORT_TYPE;
  }
  if (activity.type) {
    return activity.type;
  }
  return getActionIconIDTextAsClass(activity);
}

/**
 * Find the number of support activities on the given shift
 *
 * @param {Object} shift
 * @param {[Boolean, Array]} activities
 * @return {Number} count
 */
function getSupportActivityCount(shift, activities) {
  if (!has(shift, "activities")) {
    return 0;
  }
  if (activities === false) {
    return 0;
  }
  const supportActivities = getSupportActivities(activities);
  return filter(supportActivities, (act) =>
    shift.activities.map((v) => v.activityId).includes(act.id),
  ).length;
}

/**
 * Find the number of non- support activities on the given shift
 *
 * @param {Object} shift
 * @param {Array} activities
 * @return {Number} count
 */
function getNonSupportActivityCount(shift, activities) {
  if (!has(shift, "activities")) {
    return 0;
  }
  if (activities === false) {
    return 0;
  }
  const nonSupportActivities = getActivitiesWithoutSupport(activities);
  return filter(nonSupportActivities, (act) =>
    shift.activities.map((v) => v.activityId).includes(act.id),
  ).length;
}

/**
 * Find the number of non-support and non-care activities on the given shift
 *
 * @param {Object} shift
 * @param {Array} activities
 * @return {Number} count
 */
function getNonSupportNonCareActivityCount(shift, activities) {
  if (!has(shift, "activities")) {
    return 0;
  }
  if (activities === false) {
    return 0;
  }
  const nonSupportNonCareActivities = getActivitiesWithoutSupport(
    getActivitiesWithoutCare(activities),
  );
  return filter(nonSupportNonCareActivities, (act) =>
    shift.activities.map((v) => v.activityId).includes(act.id),
  ).length;
}

/**
 * Returns the activities that are care activities
 *
 * @param {[Boolean, Array]} activities
 * @returns {Array}
 */
function getCareActivities(activities) {
  if (activities === false) {
    return [];
  }
  return activities.filter(
    (activity) =>
      activity?.type === CARE_TYPE || activity.title === CARE_ACTIVITIES_TITLE,
  );
}

/**
 * Find the number of support activities on the given shift
 *
 * @param {Object} shift
 * @param {[Boolean, Array]} activities
 * @return {Number} count
 */
function getCareActivityCount(shift, activities) {
  if (!has(shift, "activities")) {
    return 0;
  }
  if (activities === false) {
    return 0;
  }
  const careActivities = getCareActivities(activities);
  return filter(careActivities, (act) =>
    shift.activities.map((v) => v.activityId).includes(act.id),
  ).length;
}

/**
 * Take a list of clients, activities and rota and work out
 * how many minutes clients have been assigned
 *
 * @param {Array} clients
 * @param {Array} activities
 * @param {Object} rota
 * @param {Number} viewingWeek
 *
 * @returns {Object}
 */
function clientActivityMinutes(clients, activities, rota, viewingWeek) {
  const result = {};

  // filter activities to only those that are linked to the rota
  const filteredActivities = getActivitiesAssignedInRota(activities, rota);

  // Loop through each client
  clients.forEach((client) => {
    result[client.id] = { activities: [], totalMinutes: 0 };

    // Loop through each activity
    filteredActivities.forEach((activity) => {
      if (has(activity, "clients")) {
        if (find(activity.clients, { clientId: client.id })) {
          // check the rota viewingWeek against activity.startDate
          if (
            isWithinInterval(activity.startDate, {
              start: addWeeks(rota.startDate, viewingWeek - 1),
              end: addWeeks(rota.startDate, viewingWeek),
            })
          ) {
            result[client.id].activities.push(activity);
            result[client.id].totalMinutes += differenceInMinutes(
              activity.endDate,
              activity.startDate,
            );
          }
        }
      }
    });
  });

  return result;
}

/**
 * Filter out activities based on clientIds.
 * If no clientIds are passed in then return all activites.
 *
 * @param {Array} clientIds
 * @param {Array} activities
 * @returns Array
 */
function filterActivitiesForClients(clientIds, activities) {
  if (!isArray(clientIds) || clientIds.length === 0) {
    return activities;
  }
  return activities.filter((activity) => {
    if (has(activity, "clients") && isArray(activity.clients)) {
      return activity.clients.some((client) =>
        clientIds.includes(client.clientId),
      );
    }
    return false;
  });
}

function filterActivitiesForUserId(userOrAgencyId, activities) {
  const matchingIndexes = [];
  activities.forEach((activity, index) => {
    let found = false;
    // look for a link to the user in the activity rota shifts
    get(activity, "shifts", []).forEach((shift) => {
      if (userOrAgencyId === "nobody") {
        // if assigned to a shiftId but not yet to a user or agency
        if (
          shift.shiftId &&
          shift?.userId === undefined &&
          shift?.agencyId === undefined
        ) {
          matchingIndexes.push(index);
          found = true;
        }
      } else {
        if (
          shift.userId === userOrAgencyId ||
          shift.agencyId === userOrAgencyId
        ) {
          matchingIndexes.push(index);
          found = true;
        }
      }
    });
    if (!found) {
      // look to see if the user has done any reporting on the activity
      get(activity, "clients", []).forEach((client) => {
        get(client, "reports", []).forEach((report) => {
          if (
            !found &&
            report.event !== reportEvents.BEHAVIOUR &&
            (report.userId === userOrAgencyId ||
              report.agencyId === userOrAgencyId)
          ) {
            matchingIndexes.push(index);
            found = true;
          }
        });
      });
      // look for activity.start
      if (
        !found &&
        (activity.start?.userId === userOrAgencyId ||
          /* v8 ignore next */
          activity.start?.agencyId === userOrAgencyId)
      ) {
        matchingIndexes.push(index);
      }
    }
  });
  return at(activities, matchingIndexes);
}

function getStatisticsAboutActivityCollection(activities, clientId) {
  const stats = {
    support: {
      minutes: 0,
      hours: 0,
      count: 0,
    },
    pcs: {
      minutes: 0,
      hours: 0,
      count: 0,
      ActionIconIds: [],
    },
    types: [],
  };

  activities.forEach((activity) => {
    let { startDate, endDate } = activity;
    // if we were given a clientId, and we have activity reports, use those start/end times
    // and not the scheduled start/end times
    if (clientId) {
      const client = find(activity.clients, { clientId });
      if (client !== undefined && has(client, "endDate")) {
        startDate = client.startDate;
        endDate = client.endDate;
      }
    }

    if (
      activity?.type === SUPPORT_TYPE ||
      activity.title === SUPPORT_ACTIVITIES_TITLE
    ) {
      stats.support.count += 1;
      stats.support.minutes += differenceInMinutes(endDate, startDate);
    } else {
      if (get(activity, "externalSource") === "pcs") {
        stats.pcs.count += 1;
        const minutes = differenceInMinutes(endDate, startDate);
        stats.pcs.minutes += minutes;
        const actionIconId = get(
          activity,
          "externalSourceMeta.ActionIconID",
          false,
        );
        if (actionIconId !== false) {
          const match = find(stats.pcs.ActionIconIds, { id: actionIconId });
          if (match) {
            match.count += 1;
            match.minutes += minutes;
          } else {
            stats.pcs.ActionIconIds.push({
              id: actionIconId,
              count: 1,
              minutes,
            });
          }
        }
      }
      if (has(activity, "type")) {
        const minutes = differenceInMinutes(endDate, startDate);
        const match = find(stats.types, { type: activity.type });
        if (match) {
          match.count += 1;
          match.minutes += minutes;
        } else {
          stats.types.push({
            type: activity.type,
            count: 1,
            minutes,
          });
        }
      }
    }
  });

  stats.pcs.hours = minutesToHours(stats.pcs.minutes);
  stats.pcs.ActionIconIds.forEach((actionIcon) => {
    actionIcon.hours = minutesToHours(actionIcon.minutes);
  });
  stats.types.forEach((data) => {
    data.hours = minutesToHours(data.minutes);
  });
  stats.support.hours = minutesToHours(stats.support.minutes);
  return stats;
}

/**
 * Filter the acitvities collection so they're either on the supplied rotas or not on any rota
 * Helps prevent dupicate activites where there are multiple rotas which might be in draft status
 *
 * @param {*} activities
 * @param {*} rotas
 * @returns Array
 */
function filterActivitiesForRotas(activities, rotas) {
  return filter(activities, (activity) => {
    if (!has(activity, "shifts") || activity.shifts.length === 0) {
      return true;
    }
    // this activity does have shifts associated, so it must be linked to a rota
    if (rotas.length === 1) {
      return find(activity.shifts, { rotaId: rotas[0].id }) !== undefined;
    }
    let rotaMatched = false;
    rotas.forEach((rota) => {
      if (find(activity.shifts, { rotaId: rota.id }) !== undefined) {
        rotaMatched = true;
      }
    });
    return rotaMatched;
  });
}

/**
 * Fixup (modifies in place) any old legacy activity data with new props
 *
 * @param {*} act
 */
/* v8 ignore start */
function fixupLegacyActivity(act) {
  // support activities need to have a type
  if (act.title === SUPPORT_ACTIVITIES_TITLE) {
    act.type = SUPPORT_TYPE;
  }
  // if it has shifts assigned, but no root rotaId, set it
  if (has(act, "shifts") && act.shifts.length > 0 && !has(act, "rotaId")) {
    act.rotaId = act.shifts[0].rotaId;
  }
}
/* v8 ignore stop */

const getFontAwesomeIconForActivity = (activity) => {
  switch (getActivityType(activity)) {
    case CARE_TYPE:
      if (getFontAwesomeIconFromActivity(activity) != "") {
        return getFontAwesomeIconFromActivity(activity);
      }
      return ["fas", "hand-holding-medical"];
    case SUPPORT_TYPE:
      return ["fas", "hand-holding-hand"];
    default:
      return getFontAwesomeIconFromActivity(activity);
  }
};

const getCSSClassesFromActivity = (activity, clientId, now) => {
  const extraClasses = [];

  if (activity.start === undefined) {
    const late = differenceInMinutes(now ?? new Date(), activity.startDate);
    /* c8 ignore start */
    if (late >= 45) {
      extraClasses.push("late late-45");
    } else if (late >= 30) {
      extraClasses.push("late late-30");
    } else if (late >= 15) {
      extraClasses.push("late late-15");
    }
    /* c8 ignore stop */
  }
  if (clientId) {
    const actClient = find(activity.clients, { clientId });
    if (actClient !== undefined && has(actClient, "reports")) {
      const latest = actClient.reports.slice(-1)[0];
      if (
        latest !== undefined &&
        [reportEvents.REFUSED, reportEvents.UNAVAILABLE].includes(latest.event)
      ) {
        extraClasses.push("unable-to-start");
      }
    }
  }

  const behaviourEvents = getBehaviourEventsCount(activity);

  const result = {
    activity: true,
    "not-started":
      activity.start === undefined &&
      activity.stop === undefined &&
      !extraClasses.includes("unable-to-start"),
    started: activity.start !== undefined && activity.stop === undefined,
    finished: activity.stop !== undefined,
    [getActivityType(activity)]: true,
    behaviour: behaviourEvents > 0,
    old:
      activity.start !== undefined &&
      isBefore(activity?.actualStartDate, subDays(new Date(), 1)),
  };
  extraClasses.forEach((extraClass) => {
    result[extraClass] = true;
  });

  return result;
};

const getBehaviourEventsCount = (activity) =>
  (activity.clients || []).reduce((acc, client) => {
    return (
      acc ||
      get(client, "reports", []).filter(
        (report) => report.event === "BEHAVIOUR",
      ).length
    );
  }, 0);

export {
  SUPPORT_ACTIVITIES_TITLE,
  CARE_ACTIVITIES_TITLE,
  SUPPORT_TYPE,
  CARE_TYPE,
  rotaHasActivities,
  clientActivityMinutes,
  getActivitiesForRotaDay,
  getActivitiesAssignedInRota,
  getActivitiesNotAssignedInRota,
  getSupportActivityCount,
  getCareActivityCount,
  getNonSupportActivityCount,
  getNonSupportNonCareActivityCount,
  getActivitiesWithoutSupport,
  getActivitiesWithoutCare,
  getSupportActivities,
  getCareActivities,
  getActivityType,
  filterActivitiesForClients,
  filterActivitiesForUserId,
  getStatisticsAboutActivityCollection,
  filterActivitiesForRotas,
  fixupLegacyActivity,
  getFontAwesomeIconForActivity,
  getCSSClassesFromActivity,
  getBehaviourEventsCount,
};
